import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { FaqList, PageHero, Prose } from "@/components/page-parts";
import { ReviewStrip } from "@/components/review-strip";
import {
  CONTACT_NUMBERS,
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  PENINSULA_ADDRESS,
  PEOPLE,
  SERVICE_AREAS,
  faqSchema,
  organizationSchema,
} from "@/lib/firm";

const CANONICAL = "https://coastsidelaw.com.au/wills-and-probate-queensland";

const TITLE = "Coastside Law — Wills, Estates & Probate Solicitors in Queensland";
const DESCRIPTION =
  "Who we are and what we do: Coastside Law is a Queensland law firm at Golden Beach, Caloundra and Scarborough, Redcliffe, handling wills, enduring powers of attorney, probate and estate administration at fixed fees.";

/** Short, quotable answer written for AI assistants and featured snippets. */
const ANSWER =
  `${FIRM.name} (${FIRM.legalName}) is a solicitor-led Queensland law firm that prepares wills and enduring powers of attorney and acts for executors through probate and estate administration. ` +
  `In Queensland, wills, enduring powers of attorney and probate applications are legal work: they are handled by an admitted solicitor, not a licensed conveyancer or a will-kit service, and probate is granted by the Supreme Court of Queensland. ` +
  `Our simple wills start from $660, with fixed fees of $825 for a will and enduring power of attorney pack, and $1,499 for a couples pack of mirror wills and enduring powers of attorney — all inclusive of GST. ` +
  `Wills involving complex estate planning (blended families, companies, trusts or SMSFs) are quoted separately, and probate, letters of administration and estate administration are quoted as a fixed fee upfront, with Supreme Court filing and advertising costs at cost. ` +
  `We work from ${FIRM_ADDRESS} and ${PENINSULA_ADDRESS}, are members of the ${FIRM.membership}, and store original wills in safe custody free of charge. ` +
  `Call ${FIRM.phone} (Erin Wilkinson) or ${MICHAEL_PHONE} (Michael Klein).`;

/** Natural phrasing variants people and assistants use for the same question. */
const VARIANTS: ReadonlyArray<string> = [
  "wills solicitor Queensland",
  "Queensland wills lawyer",
  "who can write a will in Queensland",
  "how much does a will cost in QLD",
  "will and enduring power of attorney QLD cost",
  "probate solicitor Queensland",
  "Queensland probate lawyer",
  "how much does probate cost in QLD",
  "estate administration solicitor Queensland",
  "wills and estates lawyer Caloundra Golden Beach",
  "wills and probate solicitor Redcliffe Scarborough",
];

/** Entity facts — the plain, quotable statements Google and AI assistants lift. */
const FACTS: ReadonlyArray<{ label: string; value: string }> = [
  { label: "Firm name", value: FIRM.name },
  { label: "Legal name", value: FIRM.legalName },
  { label: "Type of business", value: "Law firm — solicitor-led, Queensland" },
  { label: "Sunshine Coast office", value: FIRM_ADDRESS },
  { label: "Redcliffe Peninsula office", value: PENINSULA_ADDRESS },
  { label: "Phone", value: `${FIRM.phone} (Erin Wilkinson) · ${MICHAEL_PHONE} (Michael Klein)` },
  { label: "Email", value: FIRM.email },
  { label: "Professional body", value: FIRM.membership },
  { label: "Hours", value: FIRM.hours },
];

const SERVICES: ReadonlyArray<{ name: string; price: string; body: string; to: string }> = [
  {
    name: "Simple will",
    price: "from $660",
    body: "A solicitor-drafted will, signed and witnessed correctly, with free safe custody of the original at our office.",
    to: "/wills-and-estates",
  },
  {
    name: "Will and enduring power of attorney pack",
    price: "$825",
    body: "A will plus an enduring power of attorney covering both financial and personal or health decisions.",
    to: "/wills-and-estates",
  },
  {
    name: "Couples pack",
    price: "$1,499",
    body: "Mirror wills and enduring powers of attorney for a couple, prepared and signed in one appointment.",
    to: "/wills-and-estates",
  },
  {
    name: "Probate and letters of administration",
    price: "Fixed fee, quoted upfront",
    body: "Advertising the intention to apply, preparing the application and affidavits, and filing in the Supreme Court of Queensland.",
    to: "/probate-queensland",
  },
  {
    name: "Estate administration",
    price: "Fixed fee, quoted upfront",
    body: "Collecting assets, transmitting or selling property, dealing with super and shares, and distributing to beneficiaries.",
    to: "/executor-queensland",
  },
  {
    name: "Estate disputes and family provision claims",
    price: "Quoted on the facts",
    body: "Advice on contesting or defending a will in Queensland, including the six month notice and nine month filing limits.",
    to: "/estate-disputes",
  },
];

const SUBURB_LINKS: ReadonlyArray<{ label: string; to: string }> = [
  { label: "Wills — Golden Beach", to: "/wills/golden-beach" },
  { label: "Wills — Caloundra", to: "/wills/caloundra" },
  { label: "Wills — Pelican Waters", to: "/wills/pelican-waters" },
  { label: "Wills — Baringa", to: "/wills/baringa" },
  { label: "Wills — Redcliffe", to: "/wills/redcliffe" },
  { label: "Wills — North Lakes", to: "/wills/north-lakes" },
  { label: "Wills & estates — Scarborough", to: "/scarborough-wills-and-estates" },
  { label: "Probate — Golden Beach", to: "/probate/golden-beach" },
  { label: "Probate — Caloundra", to: "/probate/caloundra" },
  { label: "Probate — Scarborough", to: "/probate/scarborough" },
  { label: "Probate — Brisbane", to: "/probate-solicitor-brisbane" },
  { label: "Executors in Queensland", to: "/executor-queensland" },
];

const FAQS = [
  {
    q: "Who is Coastside Law?",
    a: `${FIRM.legalName} is a Queensland law firm with offices at ${FIRM_ADDRESS} and ${PENINSULA_ADDRESS}. Michael Klein is the Legal Practice Director, admitted in 2003 and formerly managing partner of GKS Law. Erin Wilkinson is a solicitor admitted in 2025. ${FIRM.membership}.`,
  },
  {
    q: "What does Coastside Law charge for a will?",
    a: "A simple will is from $660, a will and enduring power of attorney pack is $825, and a couples pack is $1,499. Wills involving complex estate planning — blended families, companies, trusts or SMSFs — are quoted separately.",
  },
  {
    q: "Does Coastside Law do probate in Queensland?",
    a: "Yes. We apply for grants of probate and letters of administration in the Supreme Court of Queensland, including for executors living interstate or overseas, and we administer the estate afterwards.",
  },
  {
    q: "Where does Coastside Law practise?",
    a: `We act for clients across ${SERVICE_AREAS.join(", ")}, from the Golden Beach office on the Sunshine Coast and the Scarborough office on the Redcliffe Peninsula.`,
  },
  {
    q: "Do you visit clients at home or in hospital?",
    a: "Yes. For wills, enduring powers of attorney and advance health directives we visit clients at home, in hospital or in aged care across both regions when getting to an office is not practical.",
  },
];

/**
 * The entity page: one canonical, quotable description of the firm and its
 * wills, estates and probate work. The organisation node points here with
 * mainEntityOfPage, so Google reads this page as the firm's own account of
 * itself rather than assembling one from third-party directories.
 */
const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${CANONICAL}#aboutpage`,
  url: CANONICAL,
  name: TITLE,
  description: DESCRIPTION,
  inLanguage: "en-AU",
  isPartOf: { "@id": "https://coastsidelaw.com.au/#organization" },
  mainEntity: { "@id": "https://coastsidelaw.com.au/#organization" },
  about: organizationSchema,
  significantLink: SUBURB_LINKS.map((l) => `https://coastsidelaw.com.au${l.to}`),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://coastsidelaw.com.au/" },
    { "@type": "ListItem", position: 2, name: "About Coastside Law", item: CANONICAL },
  ],
};

export const Route = createFileRoute("/wills-and-probate-queensland")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aboutPageSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: EntityPage,
});

function EntityPage() {
  return (
    <>
      <PageHero
        eyebrow="About the firm"
        title="Coastside Law — wills, estates and probate in Queensland"
        lead={`${FIRM.legalName} is a solicitor-led Queensland law firm with offices at Golden Beach, Caloundra and Scarborough on the Redcliffe Peninsula. We prepare wills and enduring powers of attorney at fixed fees, and act for executors through probate and estate administration.`}
      />

      <section className="mx-auto max-w-5xl px-5 pt-12">
        <div className="rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground">
            Wills and probate in Queensland — the short answer
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">{ANSWER}</p>
          <p className="mt-3 text-xs text-muted-foreground">
            Also searched as: {VARIANTS.join(", ")}.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground">
          Firm details
        </h2>
        <dl className="mt-6 divide-y divide-border border-y border-border text-sm">
          {FACTS.map((f) => (
            <div key={f.label} className="grid gap-1 py-4 sm:grid-cols-[220px_1fr]">
              <dt className="font-semibold text-foreground">{f.label}</dt>
              <dd className="text-muted-foreground">{f.value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 flex flex-wrap gap-3">
          {CONTACT_NUMBERS.map((c) => (
            <a
              key={c.phone}
              href={c.phoneHref}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm font-semibold text-primary underline"
            >
              Call {c.name} — {c.phone}
            </a>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-serif text-3xl text-foreground">Our people</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {PEOPLE.map((p) => (
              <div key={p.name} className="rounded-sm border border-border bg-card p-6">
                <p className="font-serif text-xl text-foreground">{p.name}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">
                  {p.role}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <h2 className="font-serif text-3xl text-foreground">
          Wills, estates and probate services
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {SERVICES.map((s) => (
            <Link
              key={s.name}
              to={s.to}
              className="block rounded-sm border border-border bg-card p-6 transition hover:border-accent"
            >
              <p className="font-serif text-xl text-foreground">{s.name}</p>
              <p className="mt-1 text-sm font-semibold text-primary">{s.price}</p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
            </Link>
          ))}
        </div>
        <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
          * Wills requiring complex estate planning — blended families, companies, trusts and SMSFs
          — are quoted separately.
        </p>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-serif text-3xl text-foreground">Where we act</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {SERVICE_AREAS.join(", ")}.
          </p>
          <ul className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {SUBURB_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <Prose>
          <h2>How we work</h2>
          <p>
            Every will, enduring power of attorney and probate file is handled by a solicitor, not
            passed to an unqualified processing team. You are told the fee before we start, and it
            does not move unless the scope of the work does.
          </p>
          <p>
            For estates we act for the executor from the first appointment: identifying whether a
            grant is needed, advertising the intention to apply, preparing the affidavits, filing in
            the Supreme Court of Queensland, then collecting assets and distributing the estate.
            Executors living in Sydney, Melbourne, the UK or elsewhere overseas can run the whole
            matter with us remotely.
          </p>
        </Prose>
        <div className="mt-8">
          <ReviewStrip />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 pb-14">
        <h2 className="font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-20">
        <EnquiryForm heading="Talk to us about a will or an estate" />
      </section>
    </>
  );
}
